import { useEffect, useState } from 'react';
import { ProtectedRoute } from './components/Auth/ProtectedRoute';
import { Dashboard } from './components/Dashboard/Dashboard';
import { AdminDashboard } from './components/Admin/AdminDashboard';
import { SubmitMember } from './components/Submit/SubmitMember';
import { UpgradePage } from './components/Upgrade/UpgradePage';
import { Landing } from './components/Landing/Landing';
import { About } from './components/About/About';
import { Gallery } from './components/Gallery/Gallery';
import { Contact } from './components/Contact/Contact';
import { Header } from './components/Header/Header';
import { Toolbar } from './components/Toolbar/Toolbar';
import { StatsSidebar } from './components/Sidebar/StatsSidebar';
import { MemberDetailSidebar } from './components/Sidebar/MemberDetailSidebar';
import { FamilyTreeView } from './components/FamilyTree/FamilyTreeView';
import { BottomNavigation } from './components/BottomNavigation/BottomNavigation';

import { useFamilyStore } from './store/familyStore';
import { useDashboardStore } from './store/dashboardStore';
import { useAuthStore } from './store/authStore';
import { currentRoute, pushRoute } from './utils/routing';

/**
 * Route map:
 *   /                        → public landing page (no auth)
 *   /family-tree/:id         → public, read-only tree (no auth)
 *   /dashboard               → protected admin dashboard (login required)
 *   /admin                   → protected admin dashboard (login required)
 *   /admin/family-tree/:id   → protected, editable tree (login required)
 *   /upgrade                 → protected
 */
function App() {
  const { selectedMember, fetchMembers, setAdminView, setSelectedMember } = useFamilyStore();
  const { familyTrees, fetchTrees } = useDashboardStore();
  const { isInitialized, initialize } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentView, setCurrentView] = useState<'landing' | 'dashboard' | 'family-tree' | 'upgrade' | 'submit' | 'admin'>('landing');
  const [isAdminRoute, setIsAdminRoute] = useState(false);
  const [currentFamilyTreeName, setCurrentFamilyTreeName] = useState<string>('');

  // Initialize auth + load trees on mount
  useEffect(() => {
    initialize();
    fetchTrees();
  }, [initialize, fetchTrees]);

  // Resolve current route into a view.
  // Extracted so both mount and popstate handlers share the same logic.
  const resolveRoute = (): void => {
    const route = currentRoute();
    // ?member=<id> deep-link: auto-select that member after load
    const memberParam = new URLSearchParams(window.location.search).get('member');

    // Admin edit route: /admin/family-tree/:id
    const adminMatch = route.match(/^\/admin\/family-tree\/([^/]+)/);
    if (adminMatch) {
      const treeId = adminMatch[1];
      const familyTree = familyTrees.find(tree => tree.id === treeId);

      if (!familyTree) {
        pushRoute('/admin');
        setCurrentView('dashboard');
        return;
      }

      setCurrentFamilyTreeName(familyTree.name);
      setCurrentView('family-tree');
      setIsAdminRoute(true);
      setAdminView(true);

      // Load members from adapter, then honor ?member= deep link
      fetchMembers(treeId).then(() => {
        if (memberParam) {
          const member = useFamilyStore.getState().members.find(m => m.id === memberParam);
          if (member) setSelectedMember(member);
        }
      });
      return;
    }

    // Public tree route: /family-tree/:id
    const publicMatch = route.match(/^\/family-tree\/([^/]+)/);
    if (publicMatch) {
      const treeId = publicMatch[1];

      setCurrentFamilyTreeName(treeId);
      setCurrentView('family-tree');
      setIsAdminRoute(false);
      setAdminView(false);

      // Load members from adapter, then honor ?member= deep link
      fetchMembers(treeId).then(() => {
        if (memberParam) {
          const member = useFamilyStore.getState().members.find(m => m.id === memberParam);
          if (member) setSelectedMember(member);
        }
      });
      return;
    }

    // /admin and /admin/* → protected admin dashboard
    if (route.startsWith('/admin')) {
      setCurrentView('admin');
      setIsAdminRoute(true);
      setAdminView(true);
      return;
    }

    // Public submission form: /submit/:treeId
    const submitMatch = route.match(/^\/submit\/([^/]+)/);
    if (submitMatch) {
      setCurrentView('submit');
      setIsAdminRoute(false);
      setAdminView(false);
      return;
    }

    if (route === '/upgrade') {
      setCurrentView('upgrade');
      setIsAdminRoute(false);
      setAdminView(false);
    } else if (route === '/dashboard') {
      setCurrentView('dashboard');
      setIsAdminRoute(false);
      setAdminView(false);
    } else if (route === '/about') {
      setCurrentView('about');
      setIsAdminRoute(false);
      setAdminView(false);
    } else if (route === '/gallery') {
      setCurrentView('gallery');
      setIsAdminRoute(false);
      setAdminView(false);
    } else if (route === '/contact') {
      setCurrentView('contact');
      setIsAdminRoute(false);
      setAdminView(false);
    } else {
      // / and everything else → public landing
      setCurrentView('landing');
      setIsAdminRoute(false);
      setAdminView(false);
    }
  };

  useEffect(() => {
    // Wait for trees to load before determining view
    if (!isInitialized) return;
    resolveRoute();
  }, [isInitialized, familyTrees]);

  const handleMenuToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Handle browser back/forward and programmatic navigation
  useEffect(() => {
    const handleRouteChange = () => {
      resolveRoute();
    };

    window.addEventListener('popstate', handleRouteChange);
    window.addEventListener('stemmagraph:route-change', handleRouteChange);
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('stemmagraph:route-change', handleRouteChange);
    };
  }, [isInitialized, familyTrees]);

  const renderFamilyTree = () => (
    <div className="h-screen flex flex-col bg-gray-50">
      <Header onMenuToggle={handleMenuToggle} familyName={currentFamilyTreeName} />

      <Toolbar
        onBackToDashboard={
          isAdminRoute
            ? () => {
                pushRoute('/admin');
                setCurrentView('dashboard');
              }
            : undefined
        }
      />

      <div className="flex flex-1 overflow-hidden relative">
        <div className={`${sidebarOpen ? 'block' : 'hidden'} lg:block`}>
          <StatsSidebar />
        </div>

        <div className="flex-1 flex flex-col">
          <FamilyTreeView />
        </div>

        {selectedMember && <MemberDetailSidebar />}
      </div>

      <BottomNavigation />
    </div>
  );

  return (
    <>
      {currentView === 'landing' && <Landing />}
      {currentView === 'about' && <About />}
      {currentView === 'gallery' && <Gallery />}
      {currentView === 'contact' && <Contact />}
      {currentView === 'dashboard' && (
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      )}
      {currentView === 'admin' && (
        <ProtectedRoute>
          <AdminDashboard />
        </ProtectedRoute>
      )}
      {currentView === 'submit' && <SubmitMember />}
      {currentView === 'upgrade' && (
        <ProtectedRoute>
          <UpgradePage />
        </ProtectedRoute>
      )}
      {currentView === 'family-tree' && (
        isAdminRoute ? (
          <ProtectedRoute>{renderFamilyTree()}</ProtectedRoute>
        ) : (
          renderFamilyTree()
        )
      )}
    </>
  );
}

export default App;
